import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  getReplaceProfiles,
  createReplaceProfile,
  updateReplaceProfile,
  deleteReplaceProfile,
  activateReplaceProfile,
  deactivateAllReplaceProfiles,
} from '../api/client'
import type { UserReplaceProfile, ReplaceProfilePayload } from '../types'
import { useStore } from '../store'
import { emptyPayload, ErrorBanner, SuccessBanner, ProfileForm, XmlTagsTable } from './ReplaceProfileComponents'

export function ReplaceProfiles() {
  const { t } = useTranslation()
  const { setActiveProfile } = useStore()
  const [profiles, setProfiles] = useState<UserReplaceProfile[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [mode, setMode] = useState<'list' | 'create' | { edit: UserReplaceProfile }>('list')
  const [deletingId, setDeletingId] = useState<number | null>(null)

  const activeProfile = profiles.find((p) => p.is_active) ?? null

  const load = async () => {
    try {
      const loaded = await getReplaceProfiles()
      setProfiles(loaded)
      setActiveProfile(loaded.find(p => p.is_active) ?? null)
    } catch (e) {
      setError(e instanceof Error ? e.message : t('replace_profiles.error_load_failed'))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { load() }, [])

  const handleCreate = async (data: ReplaceProfilePayload) => {
    await createReplaceProfile(data)
    await load()
    setMode('list')
    setSuccess(t('replace_profiles.success_created'))
  }

  const handleUpdate = async (id: number, data: ReplaceProfilePayload) => {
    await updateReplaceProfile(id, data)
    await load()
    setMode('list')
    setSuccess(t('replace_profiles.success_updated'))
  }

  const handleDelete = async (id: number) => {
    setDeletingId(id)
    try {
      await deleteReplaceProfile(id)
      await load()
      setSuccess(t('replace_profiles.success_deleted'))
    } catch (e) {
      setError(e instanceof Error ? e.message : t('replace_profiles.error_delete_failed'))
    } finally {
      setDeletingId(null)
    }
  }

  const handleActivate = async (id: number) => {
    try {
      await activateReplaceProfile(id)
      await load()
      const name = profiles.find((p) => p.id === id)?.name ?? t('replace_profiles.default_profile_name')
      setSuccess(t('replace_profiles.success_activated', { name }))
    } catch (e) {
      setError(e instanceof Error ? e.message : t('replace_profiles.error_activate_failed'))
    }
  }

  const handleDeactivateAll = async () => {
    try {
      await deactivateAllReplaceProfiles()
      await load()
      setSuccess(t('replace_profiles.success_deactivated'))
    } catch (e) {
      setError(e instanceof Error ? e.message : t('replace_profiles.error_deactivate_failed'))
    }
  }

  if (loading) {
    return <p className="text-sm text-gray-500">{t('replace_profiles.loading')}</p>
  }

  return (
    <div className="space-y-6 max-w-3xl">
      {error && <ErrorBanner msg={error} onDismiss={() => setError(null)} />}
      {success && <SuccessBanner msg={success} onDismiss={() => setSuccess(null)} />}

      {/* Actief profiel banner */}
      <div className={`rounded-xl border px-5 py-4 flex items-center justify-between ${
        activeProfile ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800/50' : 'bg-gray-50 dark:bg-gray-900 border-gray-200 dark:border-gray-700'
      }`}>
        <div>
          <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-0.5">
            {t('replace_profiles.active_banner_label')}
          </p>
          {activeProfile ? (
            <p className="text-sm font-medium text-blue-800 dark:text-blue-300">
              {activeProfile.name}
              <span className="ml-2 text-blue-500 text-xs">
                {[
                  activeProfile.project_number && `${t('replace_profiles.field_project')}: ${activeProfile.project_number}`,
                  activeProfile.sender_gln && `${t('replace_profiles.field_sender_gln_short')}: ${activeProfile.sender_gln}`,
                  activeProfile.receiver_gln && `${t('replace_profiles.field_receiver_gln_short')}: ${activeProfile.receiver_gln}`,
                ].filter(Boolean).join(' · ')}
              </span>
            </p>
          ) : (
            <p className="text-sm text-gray-500 dark:text-gray-400">{t('replace_profiles.no_active_profile')}</p>
          )}
        </div>
        {activeProfile && (
          <button
            onClick={handleDeactivateAll}
            className="text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-1.5 hover:bg-white dark:hover:bg-gray-700"
          >
            {t('replace_profiles.button_deactivate')}
          </button>
        )}
      </div>

      {mode === 'create' && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
          <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-4">{t('replace_profiles.heading_new_profile')}</h3>
          <ProfileForm
            initial={emptyPayload()}
            onSubmit={handleCreate}
            onCancel={() => setMode('list')}
            submitLabel={t('replace_profiles.button_create_profile')}
          />
        </div>
      )}

      {typeof mode === 'object' && 'edit' in mode && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-blue-200 dark:border-blue-800/50 p-6">
          <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-4">{t('replace_profiles.heading_edit_profile')}</h3>
          <ProfileForm
            initial={{
              name: mode.edit.name,
              project_number: mode.edit.project_number ?? '',
              sender_gln: mode.edit.sender_gln ?? '',
              sender_name: mode.edit.sender_name ?? '',
              receiver_gln: mode.edit.receiver_gln ?? '',
              receiver_name: mode.edit.receiver_name ?? '',
              invoicer_gln: mode.edit.invoicer_gln ?? '',
              invoicer_name: mode.edit.invoicer_name ?? '',
              invoicee_gln: mode.edit.invoicee_gln ?? '',
              invoicee_name: mode.edit.invoicee_name ?? '',
              delivery_party_gln: mode.edit.delivery_party_gln ?? '',
              delivery_party_name: mode.edit.delivery_party_name ?? '',
              ship_from_gln: mode.edit.ship_from_gln ?? '',
              ship_from_name: mode.edit.ship_from_name ?? '',
            }}
            onSubmit={(data) => handleUpdate(mode.edit.id, data)}
            onCancel={() => setMode('list')}
            submitLabel={t('replace_profiles.button_save')}
          />
        </div>
      )}

      <XmlTagsTable />

      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-800 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200">
            {t('replace_profiles.heading_profiles')} <span className="ml-1 text-gray-400 font-normal">({profiles.length})</span>
          </h3>
          {mode === 'list' && (
            <button onClick={() => setMode('create')} className="text-sm text-blue-600 hover:underline">
              {t('replace_profiles.button_new_profile')}
            </button>
          )}
        </div>
        {profiles.length === 0 ? (
          <div className="px-5 py-8 text-sm text-gray-400 dark:text-gray-500 text-center">
            {t('replace_profiles.empty_state')}
          </div>
        ) : (
          <ul className="divide-y divide-gray-100 dark:divide-gray-700">
            {profiles.map((p) => (
              <li key={p.id} className={`px-5 py-4 flex items-start gap-4 ${p.is_active ? 'bg-blue-50/50 dark:bg-blue-900/10' : ''}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{p.name}</span>
                    {p.is_active && (
                      <span className="text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 px-2 py-0.5 rounded-full font-medium">{t('replace_profiles.badge_active')}</span>
                    )}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 space-y-0.5">
                    {p.project_number && <div>{t('replace_profiles.field_project_number')} → <span className="font-mono">{p.project_number}</span></div>}
                    {(p.sender_gln || p.sender_name) && (
                      <div>
                        {t('replace_profiles.field_sender')} →{' '}
                        {p.sender_name && <span className="font-medium">{p.sender_name}</span>}
                        {p.sender_gln && <span className="font-mono ml-1">({p.sender_gln})</span>}
                      </div>
                    )}
                    {(p.receiver_gln || p.receiver_name) && (
                      <div>
                        {t('replace_profiles.field_receiver')} →{' '}
                        {p.receiver_name && <span className="font-medium">{p.receiver_name}</span>}
                        {p.receiver_gln && <span className="font-mono ml-1">({p.receiver_gln})</span>}
                      </div>
                    )}
                    {(p.invoicer_gln || p.invoicer_name) && (
                      <div>
                        {t('replace_profiles.field_invoicer')} →{' '}
                        {p.invoicer_name && <span className="font-medium">{p.invoicer_name}</span>}
                        {p.invoicer_gln && <span className="font-mono ml-1">({p.invoicer_gln})</span>}
                      </div>
                    )}
                    {(p.invoicee_gln || p.invoicee_name) && (
                      <div>
                        {t('replace_profiles.field_invoicee')} →{' '}
                        {p.invoicee_name && <span className="font-medium">{p.invoicee_name}</span>}
                        {p.invoicee_gln && <span className="font-mono ml-1">({p.invoicee_gln})</span>}
                      </div>
                    )}
                    {(p.delivery_party_gln || p.delivery_party_name) && (
                      <div>
                        {t('replace_profiles.field_delivery_party')} →{' '}
                        {p.delivery_party_name && <span className="font-medium">{p.delivery_party_name}</span>}
                        {p.delivery_party_gln && <span className="font-mono ml-1">({p.delivery_party_gln})</span>}
                      </div>
                    )}
                    {(p.ship_from_gln || p.ship_from_name) && (
                      <div>
                        {t('replace_profiles.field_ship_from')} →{' '}
                        {p.ship_from_name && <span className="font-medium">{p.ship_from_name}</span>}
                        {p.ship_from_gln && <span className="font-mono ml-1">({p.ship_from_gln})</span>}
                      </div>
                    )}
                    {!p.project_number && !p.sender_gln && !p.sender_name && !p.receiver_gln && !p.receiver_name
                      && !p.invoicer_gln && !p.invoicer_name && !p.invoicee_gln && !p.invoicee_name
                      && !p.delivery_party_gln && !p.delivery_party_name && !p.ship_from_gln && !p.ship_from_name && (
                      <span className="italic text-gray-400">{t('replace_profiles.no_replacements_set')}</span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {!p.is_active && (
                    <button onClick={() => handleActivate(p.id)} className="text-xs text-blue-600 hover:underline">
                      {t('replace_profiles.button_activate')}
                    </button>
                  )}
                  <button onClick={() => setMode({ edit: p })} className="text-xs text-gray-500 hover:text-gray-800">
                    {t('replace_profiles.button_edit')}
                  </button>
                  <button
                    onClick={() => handleDelete(p.id)}
                    disabled={deletingId === p.id}
                    className="text-xs text-red-400 hover:text-red-600 disabled:opacity-50"
                  >
                    {deletingId === p.id ? t('replace_profiles.button_deleting') : t('replace_profiles.button_delete')}
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}
